import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface User {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string;
  created_at: string;
}

interface UserEditDialogProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
}

const UserEditDialog = ({ user, isOpen, onClose, onUserUpdated }: UserEditDialogProps) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    role: 'user' as 'user' | 'admin'
  });
  const [loading, setLoading] = useState(false);
  const [showRoleConfirm, setShowRoleConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        role: user.role as 'user' | 'admin'
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Check if role is being changed
    if (formData.role !== user.role) {
      setShowRoleConfirm(true);
      return;
    }

    await updateUser();
  };

  const updateUser = async (confirmRoleChange = false) => {
    if (!user) return;

    setLoading(true);
    try {
      // Sanitize input data
      const sanitizedData = {
        first_name: formData.first_name?.trim(),
        last_name: formData.last_name?.trim(),
        phone: formData.phone?.trim().replace(/[^\d+\-\(\)\s]/g, ''), // Basic phone sanitization
      };

      // Update profile data (excluding role)
      const { error: profileError } = await supabase
        .from('profiles')
        .update(sanitizedData)
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Handle role update separately using secure function if confirmed
      if (confirmRoleChange && formData.role !== user.role) {
        const { error: roleError } = await supabase.rpc('update_user_role', {
          target_user_id: user.id,
          new_role: formData.role
        });

        if (roleError) throw roleError;
      }

      toast.success('פרטי המשתמש עודכנו בהצלחה');
      onUserUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast.error(error.message || 'שגיאה בעדכון פרטי המשתמש');
    } finally {
      setLoading(false);
      setShowRoleConfirm(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="text-right max-w-lg" dir="rtl">
        <DialogHeader>
          <DialogTitle>עריכת פרטי משתמש</DialogTitle>
        </DialogHeader>
        
        {user && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">שם פרטי</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="הזן שם פרטי"
                />
              </div>
              <div>
                <Label htmlFor="last_name">שם משפחה</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="הזן שם משפחה"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">אימייל</Label>
              <Input
                id="email"
                value={user.email}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">
                לא ניתן לשנות כתובת אימייל
              </p>
            </div>

            <div>
              <Label htmlFor="phone">טלפון</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="הזן מספר טלפון"
              />
            </div>

            <div>
              <Label htmlFor="role">תפקיד</Label>
              <Select value={formData.role} onValueChange={(value: 'user' | 'admin') => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="בחר תפקיד" />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background">
                  <SelectItem value="user">משתמש</SelectItem>
                  <SelectItem value="admin">מנהל</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>תאריך הרשמה</Label>
              <div className="text-sm text-muted-foreground p-2 bg-muted rounded">
                {new Date(user.created_at).toLocaleDateString('he-IL', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'שומר...' : 'שמור שינויים'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                ביטול
              </Button>
            </div>
          </form>
        )}

        {/* Role Change Confirmation Dialog */}
        <AlertDialog open={showRoleConfirm} onOpenChange={setShowRoleConfirm}>
          <AlertDialogContent dir="rtl">
            <AlertDialogHeader>
              <AlertDialogTitle>אישור שינוי תפקיד</AlertDialogTitle>
              <AlertDialogDescription>
                האם אתה בטוח שברצונך לשנות את תפקיד המשתמש מ"{user?.role === 'admin' ? 'מנהל' : 'משתמש'}" ל"{formData.role === 'admin' ? 'מנהל' : 'משתמש'}"?
                פעולה זו תשפיע על הרשאות המשתמש במערכת.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>ביטול</AlertDialogCancel>
              <AlertDialogAction onClick={() => updateUser(true)}>
                אישור שינוי
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export default UserEditDialog;